/**
 * SLICE C, the screen's half of the quick keys and the call log (his Care console instructions, 24 Sep
 * 2026 1:24 PM). The rules live on the server (api/src/lib/quick-keys.ts); what the screen needs of them
 * is mirrored here and pinned in LOCKSTEP by test (tests/unit/quick-keys-screen.test.tsx), so a word that
 * changes on one side fails the build instead of drifting.
 *
 * Its own module on purpose: the three console slices merge into one tree, and keeping these calls out of
 * the shared care-api.ts keeps that merge small.
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */
import { apiPost, type PostResult } from '@lib/apiClient';
import { currentClient } from '@lib/clients';

/** The presses that go to POST /api/care/quick-key (Opt In, Opt-Out and Stop are the reply presses). */
export type DoorOp = 'unstop' | 'un-opt-out' | 'resend-kit' | 'resend-results' | 'change-address' | 'explainer-video' | 'change-language';
export type ReplyOp = 'opt-in' | 'opt-out' | 'stop';
export type QuickKeyId = ReplyOp | DoorOp;
/** BB1 (22 Oct 2026): 'staff' (Care staff from the account, named operator and a reason) only on Care's own line. */
export type UnstopBasis = 'member_start' | 'his_word' | 'staff';

export interface QuickKeyDef {
  id: QuickKeyId;
  label: string;
  /** One line under the button's name in its window: what the press records. */
  help: string;
}

/** His three rows of quick keys, in his order and his words. */
export const QUICK_KEY_ROWS: ReadonlyArray<{ row: 'Status' | 'Action' | 'Explain'; keys: readonly QuickKeyDef[] }> = [
  {
    row: 'Status',
    keys: [
      { id: 'opt-in', label: 'Opt In', help: 'Approves the kit request of this text reply (the Opt In queue\'s press).' },
      { id: 'opt-out', label: 'Opt-Out', help: 'Records the opt-out of this campaign (the Opt Out queue\'s press).' },
      { id: 'stop', label: 'Stop', help: 'Records the STOP: no texts or calls on any campaign (the STOP queue\'s press).' },
      { id: 'unstop', label: 'Unstop', help: 'Lifts the member\'s STOP in Care\'s record: only after the member texted START, or on William\'s word.' },
      { id: 'un-opt-out', label: 'Un-opt-out', help: 'Lifts the member\'s opt-out of this campaign in Care\'s record.' },
    ],
  },
  {
    row: 'Action',
    keys: [
      { id: 'resend-kit', label: 'Resend Kit', help: 'Asks for a new kit. The request waits for William\'s word; nothing ships from this press.' },
      { id: 'resend-results', label: 'Resend Results', help: 'Asks for the result letter again. It waits for William\'s word; nothing is mailed from this press.' },
      { id: 'change-address', label: 'Change Address', help: 'Records the member\'s new address in the master.' },
      // ROW 640 (his R. 155; lane 05's S1-16): the language field on the case page, and its press.
      { id: 'change-language', label: 'Change Language', help: 'Records the member\'s language in the master, over the plan\'s file. When the file lists another language, the client report says so.' },
    ],
  },
  {
    row: 'Explain',
    keys: [
      { id: 'explainer-video', label: 'Send Explainer Video', help: 'Asks for an explainer video to be sent. It waits for William\'s word; nothing is sent from this press.' },
    ],
  },
];

export function quickKeyDef(id: QuickKeyId): QuickKeyDef {
  for (const r of QUICK_KEY_ROWS) for (const k of r.keys) if (k.id === id) return k;
  throw new Error(`unknown quick key ${id}`);
}

export const isReplyOp = (id: QuickKeyId): id is ReplyOp => id === 'opt-in' || id === 'opt-out' || id === 'stop';

/** The explainer videos (the server's VIDEO_TESTS, pinned). More are coming, he says. */
export const EXPLAINER_VIDEOS: ReadonlyArray<{ test: 'a1c' | 'ked' | 'fobt'; label: string }> = [
  { test: 'a1c', label: 'A1c test kit' },
  { test: 'ked', label: 'Kidney (KED) test kit' },
  { test: 'fobt', label: 'FOBT / FIT test kit' },
];
export const MORE_VIDEOS_COMING = 'More videos are coming.';

export const KIT_KINDS = ['a1c', 'ked', 'a1cked', 'fobt'] as const;
export const MAX_REASON = 300;
export const MIN_WORD_REF = 4;
export const MAX_WORD_REF = 120;

/** Which presses need a reason (the server's reasonRequired, pinned). */
export function reasonRequired(op: DoorOp): boolean {
  return op === 'unstop' || op === 'un-opt-out' || op === 'resend-kit' || op === 'resend-results' || op === 'change-language';
}

/**
 * ROW 640: the languages she may record, in the plan's own words (the server's MEMBER_LANGUAGES, pinned in
 * lockstep). COMBODIAN is the plan's spelling; the label reads Cambodian.
 */
export const MEMBER_LANGUAGES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'ENGLISH', label: 'English' },
  { value: 'SPANISH', label: 'Spanish' },
  { value: 'VIETNAMESE', label: 'Vietnamese' },
  { value: 'CHINESE', label: 'Chinese' },
  { value: 'CANTONESE', label: 'Cantonese' },
  { value: 'MANDARIN', label: 'Mandarin' },
  { value: 'RUSSIAN', label: 'Russian' },
  { value: 'KOREAN', label: 'Korean' },
  { value: 'TAGALOG', label: 'Tagalog' },
  { value: 'COMBODIAN', label: 'Cambodian' },
  { value: 'OTTOMAN LANGUAGES', label: 'Ottoman languages' },
  { value: 'HINDI', label: 'Hindi' },
  { value: 'PORTUGUESE', label: 'Portuguese' },
  { value: 'ARABIC', label: 'Arabic' },
  { value: 'JAPANESE', label: 'Japanese' },
  { value: 'ARMENIAN', label: 'Armenian' },
  { value: 'FRENCH', label: 'French' },
];

/** The words the case page shows for the language on file (the server's languageLabel, pinned). */
export function languageLabel(value: string): string {
  const v = value.trim().toUpperCase();
  if (!v || v === 'UNDETERMINED') return 'no language';
  const hit = MEMBER_LANGUAGES.find((l) => l.value === v);
  return hit ? hit.label : v.toLowerCase().replace(/\b[a-z]/g, (ch) => ch.toUpperCase());
}

/* ------------------------------------------ the call log ------------------------------------------ */

/** The server's CALL_OUTCOMES, pinned: the boxes she ticks after a call, in two groups. */
export const CALL_OUTCOMES: ReadonlyArray<{ key: string; label: string; reached: boolean }> = [
  { key: 'no_answer', label: 'No answer', reached: false },
  { key: 'voicemail', label: 'Left voicemail', reached: false },
  { key: 'busy', label: 'Busy', reached: false },
  { key: 'wrong_number', label: 'Wrong number', reached: false },
  { key: 'not_in_service', label: 'Number not in service', reached: false },
  { key: 'reached', label: 'Spoke with the member', reached: true },
  { key: 'call_back', label: 'Asked for a call back', reached: true },
  { key: 'wants_kit', label: 'Wants a kit', reached: true },
  { key: 'already_done', label: 'Already did the test', reached: true },
  { key: 'declined', label: 'Does not want the test', reached: true },
  { key: 'do_not_call', label: 'Asked not to be called', reached: true },
  { key: 'other_language', label: 'Needs another language', reached: true },
];

/** What she says first when the member answers (the server's RECORDED_LINE_SCRIPT, pinned). */
export const RECORDED_LINE_SCRIPT = 'This call is on a recorded line.';

export function callOutcomeLabel(key: string): string {
  return CALL_OUTCOMES.find((o) => o.key === key)?.label ?? key;
}

/**
 * The same checks the server makes, so the button says what is missing before she presses it: at least
 * one box; the two groups never mix; a call that reached the member needs the recorded line and notes.
 * '' when the call can be logged.
 */
export function callLogProblem(outcomes: readonly string[], note: string, recordedLineSaid: boolean): string {
  if (!outcomes.length) return 'Tick at least one box: what happened on the call.';
  const reached = outcomes.filter((o) => CALL_OUTCOMES.find((c) => c.key === o)?.reached);
  if (reached.length && reached.length !== outcomes.length) return 'A call either reached the member or did not: untick one group.';
  if (reached.length && !recordedLineSaid) return `Tick that you said "${RECORDED_LINE_SCRIPT}"`;
  if (reached.length && !note.trim()) return 'A call that reached the member needs its notes.';
  return '';
}

/* ------------------------------------------ the calls ------------------------------------------ */

export interface DoorPressResp {
  ok: boolean;
  /** `staged` (wave 3 item 4, and the queue's buttons of 16 Oct 2026): queued on the case for approval. */
  status: 'held' | 'refused' | 'unknown' | 'staged';
  staged?: boolean;
  message?: string;
  code?: string;
  retry?: 'same-id' | 'none';
  askMember?: string;
  master?: { ref: string; state?: string; replayed?: boolean };
  item?: Record<string, unknown>;
}

export interface QuickKeyArgs {
  eventId: string;
  op: DoorOp;
  clientRequestId: string;
  reason?: string;
  basis?: UnstopBasis;
  wordRef?: string;
  campaignId?: string;
  kitKind?: string;
  address?: { line1: string; line2?: string; city: string; state: string; zip: string; zip4?: string };
  test?: string;
  language?: string;
  memberLanguage?: string;
  initials?: string;
  /** 16 Oct 2026: the queue's buttons send `true`, so the press is queued for Final Review (never false). */
  toApproval?: true;
}

export function quickKey(args: QuickKeyArgs): Promise<PostResult<DoorPressResp>> {
  return apiPost(`/api/care/quick-key`, { client: currentClient(), ...args });
}

/**
 * BB1 (22 Oct 2026): the Lookup's Unstop names the MEMBER, not a case; the server queues it on the member's newest
 * open case for Final Review (MEMBERCARE_UNSTOP_CARE_LINE on, master link off), or answers no_open_case.
 */
export interface LookupUnstopArgs {
  memberId: string;
  basis: UnstopBasis;
  reason: string;
  wordRef?: string;
  clientRequestId: string;
}
export function lookupUnstop(args: LookupUnstopArgs): Promise<PostResult<DoorPressResp>> {
  return apiPost(`/api/care/quick-key`, { client: currentClient(), op: 'unstop', ...args });
}

/** Whether the Lookup card offers the Unstop: the switch is on and the card shows the member's STOP. Pure. */
export function lookupUnstopShown(unstopCareLine: boolean, optOutClass: string | null | undefined): boolean {
  return unstopCareLine && String(optOutClass ?? '').toUpperCase() === 'STOP';
}

export interface CallLogArgs {
  eventId: string;
  outcomes: string[];
  note: string;
  recordedLineSaid: boolean;
  startedAt?: string;
  clientRequestId: string;
  initials?: string;
}

export function callLog(args: CallLogArgs): Promise<PostResult<DoorPressResp>> {
  return apiPost(`/api/care/call-log`, { client: currentClient(), ...args });
}

/** The sentence a press's answer prints, whatever the answer. */
export function pressSentenceOf(r: PostResult<DoorPressResp>): { tone: 'ok' | 'warn' | 'danger'; text: string } {
  const d = r.data as DoorPressResp & { error?: string };
  if (r.ok && d.status === 'held') return { tone: 'ok', text: d.message ?? 'Done.' };
  if (d.retry === 'same-id') return { tone: 'warn', text: `${d.message ?? 'No answer from the master.'} Press again: the same press is sent, never twice.` };
  return { tone: 'danger', text: String(d.message ?? d.error ?? `The press was refused (${r.status}).`) };
}

/**
 * His answers (2 Oct 2026, idea 3: "Return all of them (put them where they belong)"): the keys that
 * have no checkbox in the Action / disposition list, by the group of his map they belong to. Each is
 * the same quick-key press as before. Never a reply press (those are the reply panel's own).
 */
export const GROUP_KEYS: Readonly<Record<string, readonly QuickKeyId[]>> = {
  'Change information': ['change-language'],
  Opting: ['unstop', 'un-opt-out'],
  Kits: ['explainer-video'],
};

/**
 * L1 (William, 16 Oct 2026 about 10 PM: "hide them, fix the note"). Every key above goes to the master's
 * door, and the server refuses each one with a 409 while the master link (MEMBERCARE_MASTER_DECISIONS, K4)
 * is off. So the drawer shows them only when the server says the link is on (features.masterDecisions,
 * false until the sign-in read lands), and the note under the list says what really happens.
 */
export function groupKeysFor(group: string, masterDecisions: boolean, unstopCareLine = false): readonly QuickKeyId[] | undefined {
  if (masterDecisions) return GROUP_KEYS[group];
  // BB1 (22 Oct 2026, MEMBERCARE_UNSTOP_CARE_LINE): with the link off, Unstop alone shows under Opting; it goes to Final
  // Review and its approval writes Care's own line. Off: hidden, exactly as before.
  return unstopCareLine && group === 'Opting' ? UNSTOP_ONLY : undefined;
}
const UNSTOP_ONLY: readonly QuickKeyId[] = ['unstop'];

/** BB1: the Unstop panel's words on Care's own line (drawer and Lookup). */
export const UNSTOP_CARE_LINE_WORDING = {
  staff: 'Care staff from the account (your name is recorded; the reason is required)',
  waits: 'Goes to Final Review on the case. Once approved, the STOP is lifted on the member\'s file with your name and reason. The phone company keeps its block until the member texts START.',
  lookupNoCase: 'This member has no open Care case: the Unstop waits for Final Review on a case, so open or record a case for the member first.',
} as const;

/**
 * PART G (William, 16 Oct 2026 about 11:15 PM, on the note under Consent update: "Remove the langauge"). While
 * the link is off the four keys stay hidden and NOTHING is said about them: keysNote answers '' and the drawer
 * renders no note. The two link-on sentences stay, shown only while the four keys show.
 */
export const KEYS_NOTE_WORDING = {
  /** The link is on and presses wait for approval. */
  onStaged: 'Change Language, Unstop, Un-opt-out and Send Explainer Video go to Final Review first. Once approved, each is recorded in the main database with your name. Nothing is sent to the member from them.',
  /** The link is on and presses act when pressed. */
  onNow: 'Change Language, Unstop, Un-opt-out and Send Explainer Video are recorded in the main database with your name when pressed. Nothing is sent to the member from them.',
} as const;

export function keysNote(masterDecisions: boolean, stagedPresses: boolean): string {
  if (!masterDecisions) return '';
  return stagedPresses ? KEYS_NOTE_WORDING.onStaged : KEYS_NOTE_WORDING.onNow;
}
